import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SubmitButton } from "@/components/ui/submit-button";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler, formatCNPJ, formatPhone } from "@/lib/utils";
import { clientProviderService } from "@/services/api/clientProviders";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const formSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  cnpj: z.string().optional(),
  address: z.string().optional(),
  state_registration: z.string().optional(),
  type: z.number(),
  banner: z.string().optional(),
  phone: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const ClientProvidersForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showPhoneField, setShowPhoneField] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      cnpj: "",
      address: "",
      state_registration: "",
      type: 1,
      phone: "",
    },
  });

  const providerType = form.watch("type");

  useEffect(() => {
    setShowPhoneField(providerType === 2);
  }, [providerType]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: clientProviderService.store,
    updateService: clientProviderService.update,
    onSuccess: () => navigate("/client-providers"),
  });

  useEffect(() => {
    const loadClientProvider = async () => {
      if (id) {
        try {
          const response = await clientProviderService.show(Number(id));
          const providerData = response.data;
          form.reset({
            name: providerData.name,
            cnpj: providerData.cnpj ? formatCNPJ(providerData.cnpj) : "",
            address: providerData.address || "",
            state_registration: providerData.state_registration || "",
            type: providerData.type,
            phone: providerData.phone ? formatPhone(providerData.phone) : "",
          });
          setShowPhoneField(providerData.type === 2);
        } catch (err) {
          errorHandler(err, "Erro ao carregar fornecedor");
          navigate("/client-providers");
        }
      }
      setShowSkeleton(false);
    };

    loadClientProvider();
  }, [id, navigate, form]);

  const removeCNPJMask = (cnpj: string) => {
    if (!cnpj) return "";
    return cnpj.replace(/\D/g, "");
  };

  const removePhoneMask = (phone: string) => {
    if (!phone) return "";
    return phone.replace(/\D/g, "");
  };

  const onSubmit = async (values: FormValues) => {
    const cleanedValues = {
      ...values,
      cnpj: values.cnpj ? removeCNPJMask(values.cnpj) : "",
      address: values.address || "",
      state_registration: values.state_registration || "",
      phone: values.phone ? removePhoneMask(values.phone) : undefined,
    };

    if (values.type !== 2) {
      delete cleanedValues.phone;
    }

    await handleSubmit(cleanedValues);
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Fornecedor" : "Novo Fornecedor"}</CardTitle>
      </CardHeader>
      <CardContent>
        {showSkeleton ? (
          <div className="space-y-4">
            <div className="h-10 bg-muted animate-pulse rounded-md"></div>
            <div className="h-10 bg-muted animate-pulse rounded-md"></div>
            <div className="h-10 bg-muted animate-pulse rounded-md"></div>
            <div className="h-10 bg-muted animate-pulse rounded-md"></div>
            <div className="h-10 bg-muted animate-pulse rounded-md"></div>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CNPJ</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="00.000.000/0000-00"
                        {...field}
                        maxLength={18}
                        onChange={(e) => {
                          const formatted = formatCNPJ(e.target.value);
                          field.onChange(formatted);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Endereço</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state_registration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Inscrição Estadual</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={String(field.value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">Abastecimento</SelectItem>
                        <SelectItem value="2">Manutenção/Reparo</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {showPhoneField && (
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="(00) 0000-0000"
                          {...field}
                          maxLength={15}
                          onChange={(e) => {
                            const formatted = formatPhone(e.target.value);
                            field.onChange(formatted);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <div className="flex justify-end space-x-2 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/client-providers")}
                >
                  Cancelar
                </Button>
                <SubmitButton loading={loading} />
              </div>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientProvidersForm;
